<template>
  <div :class="columnClassName">
    <column-header v-if="showColumnHeader" :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <lock-info v-if="locked" :locked="locked" :lockStatus="lockStatus" v-on:forceLock="getComponentLock(true)" />
      <div class="row">
				<div class="col-md-12">
          <h3>{{ layout.name }}</h3>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-4 leftcol">
          <div class="form-group languageversioned">
            <label for="name">{{ _('Name') }}</label>
            <input type="text" class="form-control" id="layoutName" v-model="layout.name" :placeholder="_('Enter layout section name')">
          </div>
        </div>
        <div class="col-sm-4">
          <div class="form-group">
            <label for="icon">{{ _('Icon') }}</label><br />
            <button class="btn btn-default" v-on:click="selectIconDialog">{{ _('Select icon') }}</button>
            <i v-if="icon" :class="'fa fa-fw fa-2x fa-' + icon"></i>
            <button v-if="icon" class="btn btn-danger" v-on:click="removeIcon">{{ _('Remove icon') }}</button>
          </div>
        </div>
        <div class="col-sm-4 rightcol">
          <div class="pull-right">
            <button type="button" class="btn btn-info" id="addColumnButton" v-on:click="addColumnDialog"><i class="fa fa-fw fa-plus"></i> {{ _('Add column') }}</button>
          </div>
        </div>
      </div>
      <div class="row" id="layoutContainer">
        <column v-for="col in data.columns" :key="col.identifier" :col="col" :components="componentLayouts[col.identifier]" v-on:componentOrderChanged="componentOrderChanged" v-on:removeColumn="removeColumn" v-on:removeComponent="removeComponent" v-on:addComponentDialog="addComponentDialog" />
      </div>
      <div v-if="isUiFeatureTemplate" class="row" style="margin-top: 24px">
        <div class="col-md-12">
          <h4>{{ _('Ready-made screen') }}</h4>
          <p class="help-block">{{ _('Offer this layout section in the catalog so shops can add it to a user interface.') }}</p>
          <div class="checkbox">
            <label>
              <input type="checkbox" v-model="offerAsScreen" />
              {{ _('Offer as a ready-made screen') }}
            </label>
          </div>
          <template v-if="offerAsScreen">
            <div class="row">
              <div class="col-sm-4">
                <div class="form-group">
                  <label for="recipeId">{{ _('Recipe id') }}</label>
                  <input id="recipeId" type="text" class="form-control" v-model="recipeId" maxlength="64" />
                  <p class="help-block">{{ _('Letters and numbers only. Do not change this after shops have added the screen.') }}</p>
                </div>
              </div>
              <div class="col-sm-8">
                <div class="form-group languageversioned">
                  <label for="recipeDescription">{{ _('Short description') }}</label>
                  <textarea id="recipeDescription" class="form-control" rows="3" maxlength="500" v-model="recipeDescription"></textarea>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <div class="button-col">
          <button class="btn btn-danger" type="button" v-on:click="remove" v-if="layout.id"><i class="fa fa-fw fa-trash"></i><span class="hidden-xs"> {{ _('Delete') }}</span></button>
          <button v-if="showUnlinkButton" class="btn btn-warning" type="button" v-on:click="unlinkLayout"><i class="fa fa-fw fa-chain-broken"></i> {{ _('Remove from menu') }}</button>
        </div>
        <div class="button-col">
          <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
          <button class="btn btn-default" type="button" v-on:click="close"><i class="fa fa-fw fa-close"></i><span class="hidden-xs"> {{ _('Close') }}</span></button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>

import axios from 'axios'
import components from '~/lib/components'
import settingshelpers from '~/lib/settingshelpers'
import { LayoutDetailsColumn } from '#components'

export default {
  data () {
    return {
      layout: {},
      allComponents: [],
      data: {
        columns: []
      },
      componentLayouts: {}
    }
  },
  mixins: [components, settingshelpers],
  computed: {
    icon: {
      get () {
        return this.data ? this.data.icon : null
      },
      set (newValue) {
        this.data.icon = newValue
      }
    },
    offerAsScreen: {
      get () {
        return !!(this.data && this.data.offerAsScreen)
      },
      set (value) {
        this.data.offerAsScreen = !!value
        if (value && !this.recipeId) {
          this.recipeId = this.recipeIdFromName(this.layout.name)
        }
      }
    },
    recipeId: {
      get () {
        return this.data && this.data.recipeId ? this.data.recipeId : ''
      },
      set (value) {
        this.data.recipeId = this.transliterateRecipeId(value)
      }
    },
    recipeDescription: {
      get () {
        return this.data && this.data.description ? this.data.description : ''
      },
      set (value) {
        this.data.description = value
      }
    },
    showUnlinkButton () {
      if (this.component && this.component.initData && this.component.initData.modal) {
        return true
      }
      return false
    }
  },
  methods: {
    transliterateRecipeId (value) {
      return String(value || '')
        .toLowerCase()
        .replace(/ä/g, 'a')
        .replace(/ö/g, 'o')
        .replace(/å/g, 'a')
        .replace(/[^a-z0-9]/g, '')
    },
    recipeIdFromName (name) {
      const slug = this.transliterateRecipeId(name)
      if (!slug || !/^[a-z]/.test(slug)) {
        return ('screen' + slug).substring(0, 64)
      }
      return slug.substring(0, 64)
    },
    unlinkLayout () {
      this.$emit('unlinkLayout', this.layout.id)
    },
    async componentOrderChanged (data, event) {
      // This incredible hacky shit seems to be necessary to trigger any sort of reactivity in this.componentLayouts? WTF Bungie
      this.componentLayouts = JSON.parse(JSON.stringify(this.componentLayouts))
      let index = 1
      data.components.forEach(c => { c.index = String(index++); c.column = data.column })
      this.componentLayouts[data.column] = [...data.components]
    },
    selectIconDialog () {
      this.showDialog({
        title: this._('Select icon'),
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            handler: (data) => {
              this.icon = data.icon
            }
          }, 
        ],
        fields: [
          {
            title: this._('Icon'),
            type: 'icon',
            name: 'icon',
            defaultValue: this.icon
          }
        ]
      })
    },
    removeIcon () {
      this.icon = null
    },
    addColumnDialog () {
      this.showDialog({
        title: this._('Add column'),
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            handler: this.addColumn
          }, 
        ],
        fields: [
          {
            title: this._('Identifier'),
            type: 'text',
            name: 'identifier',
            placeholder: this._('Enter an identifier for the new column')
          }
        ]
      })
    },
    addColumn (data) {
      if (!this.data.columns) {
        this.data.columns = []
      }
      this.data.columns.push({ identifier: data.identifier, width: 4 })
    },
    removeColumn (col) {
      this.data.columns = this.data.columns.filter(c => c.identifier !== col.identifier)
      this.layout.ownComponent_layout = this.layout.ownComponent_layout.filter(l => l.column !== col.identifier)
    },
    async addComponentDialog (col) {
      const options = this.allComponents.map(c => { return { value: c.id, name: c.name }})
      this.showDialog({
        title: this._('Add component'),
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            handler: this.addComponent(col)
          }, 
        ],
        fields: [
          {
            title: this._('Component'),
            type: 'select',
            name: 'component',
            placeholder: this._('Select a component'),
            options: options
          }
        ]
      })
    },
    addComponent (col) {
      return (data) => {
        const c = {
          column: col.identifier,
          component_id: data.component,
          component: this.allComponents.find(c => parseInt(c.id, 10) === parseInt(data.component))
        }
        this.layout.ownComponent_layout.push(c)
        this.updateComponentLayouts()
      }
    },
    removeComponent (link) {
      this.layout.ownComponent_layout = this.layout.ownComponent_layout.filter(l => !(l.column === link.column && l.component_id === link.component.id))
      this.updateComponentLayouts()
    },
    getSaveData () {
      const exportFields = [
        'id',
        'name'
      ]
      const data = exportFields.reduce((a, e) => (a[e] = this.layout[e], a), {})
      data.data = JSON.stringify(this.data)
      data.components = JSON.stringify(this.layout.ownComponent_layout)
      data.version = this.version
      return data
    },
    async doRemove () {
      let response
      try {
        response = await axios.delete(this.baseDomain + this.shopId + '/layouts/' + this.layout.id + '.json?apikey=' + this.apiKey)
      } catch(e) {
        return this.showError(e)
      }
      this.close()
    },
    async save () {
      this.saving = true
      const data = this.getSaveData()
      let response
      try {
        if (data.id) {
          response = await axios.put(this.baseDomain + this.shopId + '/layouts/' + data.id + '.json?apikey=' + this.apiKey, data)
        } else {
          delete data.id
          response = await axios.post(this.baseDomain + this.shopId + '/layouts.json?apikey=' + this.apiKey, data)
        }
      } catch(e) {
        this.saving = false
        return this.showError(e)
      }
      if (response && response.data && response.data.id) {
        await this.loadLayout(response.data.id)
      }
      this.saving = false
      this.saved = true
      setTimeout(() => { this.saved = false }, 1000)
      this.showNotification(this._('Changes saved successfully!'))
      this.$emit('save', this.layout)
      if (this.saveShouldClose) {
        this.close()
      }
    },
    async reloadOnVersionChange () {
      if (!this.layout || !this.layout.id) {
        return
      }
      await this.loadLayout(this.layout.id)
    },
    loadLayout (id) {
      return new Promise(async (resolve, reject) => {
        this.loading = true
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/layouts/' + id + '.json?apikey=' + this.apiKey + this.versionStr)
        } catch (e) {
          this.showError(e)
          reject()
          this.loading = false
          return
        }
        this.layout = response.data
        if (this.layout.data) {
          this.data = JSON.parse(this.layout.data)
          this.updateComponentLayouts()
        }
        this.storeState()
        this.loading = false
        resolve(this.layout)
      })
    },
    async loadComponents () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/components.json?iSortingCols=1&iSortCol_0=1&sSortDir=asc&apikey=' + this.apiKey)
      } catch(e) {
        this.showError(e)
        return
      }
      this.allComponents = response.data
    },
    updateComponentLayouts () {
      if (!this.data || !this.data.columns) {
        return
      }
      this.componentLayouts = JSON.parse(JSON.stringify(this.componentLayouts))
      for (let i = 0; i < this.data.columns.length; i++) {
        const col = this.data.columns[i]
        this.componentLayouts[col.identifier] = [...this.layout.ownComponent_layout.filter(l => l.column === col.identifier)]
      }
    }
  },
  watch: {
    'layout.name' (name, previous) {
      if (!this.offerAsScreen || !this.data) {
        return
      }
      const previousAuto = this.recipeIdFromName(previous)
      if (!this.recipeId || this.recipeId === previousAuto) {
        this.recipeId = this.recipeIdFromName(name)
      }
    }
  },
  components: {
    'column': LayoutDetailsColumn
  },
  created () {
    if (this.component && this.component.initData) {
      this.loadLayout(this.component.initData.layoutId)
    }
  },
  mounted () {
    this.loadComponents()
    if(!this.events) {
      let events = {}
      events['layoutUpdate'] = async (message) => {
        if (message && message.data && this.layout && parseInt(message.data.id, 10) === parseInt(this.layout.id, 10)) {
          if (!this.unsavedChanges) { // Only update if we haven't made any changes
            this.loadLayout(message.data.id)
          }
        }
      }
      this.events = events
    }
    this.enableEventBus()
  },
  async updated () {
    if (this.component && this.component.initData) {
      if (this.layout && this.layout.id && parseInt(this.layout.id, 10) !== parseInt(this.component.initData.layoutId, 10)) {
        try {
          await this.loadLayout(this.component.initData.layoutId)
        } catch(e) {}
      }
    }
  }
}
</script>